import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router";
import { Box, Typography, Alert, Button } from "@mui/material";
import { Login as LoginIcon } from "@mui/icons-material";
import { useAuth } from "../AuthProvider";
import LoadingState from "../../components/states/LoadingState";
import { colors } from "../../styles/theme";

/**
 * Startet den Auth0-Login-Flow für nicht-authentifizierte Nutzer.
 *
 * Liest – falls vorhanden – die Auth0-Invitation-Parameter (`invitation`,
 * `organization`, `organization_name`) aus der URL-Query und reicht sie an den
 * Login-Flow weiter. Damit dient diese Seite gleichzeitig als Einstiegspunkt
 * für Auth0-Einladungs-Links (Application Login URI bzw. Tenant Login URI).
 */
const AutoLoginPage: React.FC = () => {
  const { login } = useAuth();
  const location = useLocation();
  const redirectInitiated = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const startLogin = useCallback(() => {
    redirectInitiated.current = true;
    setError(null);

    const params = new URLSearchParams(location.search);
    const invitation = params.get("invitation") ?? undefined;
    const organization = params.get("organization") ?? undefined;
    const organizationName = params.get("organization_name") ?? undefined;

    login({ invitation, organization, organizationName }).catch(() => {
      redirectInitiated.current = false;
      setError("Weiterleitung zur Anmeldung fehlgeschlagen.");
    });
  }, [login, location.search]);

  useEffect(() => {
    if (redirectInitiated.current) return;
    startLogin();
  }, [startLogin]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.primary} 100%)`,
          padding: 2,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            background: colors.glass.backgroundStrong,
            padding: 4,
            borderRadius: 3,
            backdropFilter: "blur(10px)",
            border: `1px solid ${colors.glass.borderSubtle}`,
            maxWidth: 400,
            width: "100%",
          }}
        >
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {error}
          </Alert>

          <Button
            variant="outlined"
            startIcon={<LoginIcon />}
            onClick={startLogin}
            sx={{ mt: 1, borderRadius: 2, textTransform: "none" }}
          >
            Erneut versuchen
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <LoadingState
      fullScreen={true}
      message="Weiterleitung zur Anmeldung..."
      subtitle="Bitte warten Sie einen Moment."
    />
  );
};

export default AutoLoginPage;
